import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, CircularProgress, Avatar, Chip, alpha, useTheme } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useGetReplacementStaffMutation } from "../../../Api/attoDeskApi";

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};
const initials = (n: string) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

export interface ReplacementPick { staffId: number; start: string; end: string }

/**
 * Smart replacement-staff selector for a class-level makeup slot. Queries the
 * real availability endpoint whenever date/time/mode/location change, ignores
 * stale responses, never pre-selects the original teacher, and offers real
 * alternative windows when nobody is free at the requested time.
 */
const ReplacementStaffPicker: React.FC<{
  occurrenceId: number | string;
  makeupDate: string;              // "YYYY-MM-DD"
  startTime: string;               // "HH:mm"
  endTime: string;                 // "HH:mm"
  studyMode: string;               // "online" | "in_person"
  locationId?: number | string | null;
  value: string;                   // selected staffId (string) or ""
  onChange: (staffId: string) => void;
  onPickAlternative?: (p: ReplacementPick) => void;
  /**
   * The slot is immovable (Switch Staff keeps the occurrence's own date/time), so
   * "pick a free time instead" suggestions are not actionable and are suppressed.
   * Makeup scheduling leaves this false — there the time IS negotiable.
   */
  fixedSlot?: boolean;
  /** Overrides the "Replacement Staff *" caption. */
  label?: string;
}> = ({ occurrenceId, makeupDate, startTime, endTime, studyMode, locationId, value, onChange, onPickAlternative, fixedSlot, label }) => {
  const theme = useTheme();
  const [getReplacement, { isLoading }] = useGetReplacementStaffMutation();
  const [data, setData] = useState<any>(null);
  const seqRef = useRef(0);

  const ready = !!makeupDate && !!startTime && !!endTime && endTime > startTime;

  useEffect(() => {
    if (!ready) { setData(null); return; }
    const seq = ++seqRef.current;
    let alive = true;
    (async () => {
      try {
        const res = await getReplacement({
          occurrenceId, makeup_date: makeupDate, start_time: startTime, end_time: endTime,
          study_mode: studyMode, location_id: studyMode === "in_person" ? Number(locationId) || undefined : undefined,
        }).unwrap();
        if (!alive || seq !== seqRef.current) return; // ignore stale
        setData(res.status ? res.data : null);
      } catch { if (alive && seq === seqRef.current) setData(null); }
    })();
    return () => { alive = false; };
  }, [occurrenceId, makeupDate, startTime, endTime, studyMode, locationId, ready, getReplacement]);

  const available: any[] = useMemo(() => (Array.isArray(data?.available_staff) ? data.available_staff : []), [data]);
  const busy: any[] = useMemo(() => (Array.isArray(data?.unavailable_staff_suggestions) ? data.unavailable_staff_suggestions : []), [data]);
  const alternatives: any[] = useMemo(() => (Array.isArray(data?.alternative_times) ? data.alternative_times : []), [data]);

  // Auto-clear a selection that is no longer among the available staff at this slot.
  useEffect(() => {
    if (!value) return;
    if (!ready) { onChange(""); return; }
    if (data && !available.some((s) => String(s.id) === String(value))) onChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ready]);

  const Label = <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>{label ?? "Replacement Staff *"}</Typography>;

  if (!ready) {
    return <Box>{Label}<Typography sx={{ fontSize: 12, color: "text.secondary" }}>Select a valid date and time to view available staff.</Typography></Box>;
  }
  if (isLoading) {
    return <Box>{Label}<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}><CircularProgress size={16} /><Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Checking matching staff…</Typography></Box></Box>;
  }

  return (
    <Box>
      {Label}
      {available.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {available.map((s) => {
            const on = String(s.id) === String(value);
            const gs = (s.matched_grade_subjects || []).map((g: any) => `${g.grade} ${(g.subjects || []).join(" & ")}`).join(" · ");
            return (
              <Box key={s.id} onClick={() => onChange(String(s.id))}
                sx={{ p: 1, borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 1.25,
                  border: `1px solid ${on ? theme.palette.primary.main : "rgba(15,23,42,0.12)"}`, bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : "#fff" }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>{initials(s.name)}</Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{s.name}</Typography>
                    <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />} label="Exact match" size="small"
                      sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.main, "& .MuiChip-icon": { color: theme.palette.success.main } }} />
                    {s.is_original && <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>· original</Typography>}
                  </Box>
                  {gs && <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{gs}</Typography>}
                  <Typography sx={{ fontSize: 11, color: "success.main", fontWeight: 600 }}>Available {fmtTime(startTime)}–{fmtTime(endTime)} · {studyMode === "online" ? "Online" : "In Person"}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No matching staff are available at the selected time.</Typography>
      )}

      {/* Suitable-but-busy → real next-free windows (only when the time can move) */}
      {!!busy.length && !fixedSlot && (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
            <EventBusyOutlinedIcon sx={{ fontSize: 14 }} /> Suitable, but busy — pick a free time
          </Typography>
          {busy.map((s) => (
            <Box key={s.id} sx={{ mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{s.name}</Typography>
              {!!s.next_free?.length && (
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.3 }}>
                  {s.next_free.map((nf: any, i: number) => (
                    <Chip key={i} size="small" label={`${fmtTime(nf.start_time)}–${fmtTime(nf.end_time)}`}
                      onClick={() => onPickAlternative?.({ staffId: s.id, start: nf.start_time, end: nf.end_time })}
                      sx={{ height: 24, fontSize: 11, cursor: "pointer", bgcolor: alpha("#0f172a", 0.05) }} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Cross-staff alternative windows */}
      {!available.length && !!alternatives.length && !fixedSlot && (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 700, mb: 0.5 }}>Suggested alternative times</Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {alternatives.map((a, i) => (
              <Chip key={i} size="small" label={`${fmtTime(a.start_time)}–${fmtTime(a.end_time)} · ${a.staff_name}`}
                onClick={() => onPickAlternative?.({ staffId: a.staff_id, start: a.start_time, end: a.end_time })}
                sx={{ height: 26, fontSize: 11, cursor: "pointer", bgcolor: alpha("#0f172a", 0.05) }} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReplacementStaffPicker;
